// Language-aware drop-ins for react-router's Link and useNavigate.
//
// On a translated page (/hi/pricing) a link to "/gyms" goes to "/hi/gyms" when that page is
// translated, and to the English "/gyms" otherwise. On English pages nothing changes.
// Import Link / useNavigate from here instead of 'react-router' in site components.

import { forwardRef, useCallback } from 'react';
import { Link as RouterLink, useNavigate as useRouterNavigate, type LinkProps, type NavigateOptions, type To } from 'react-router';
import { useLanguage } from '../contexts/LanguageContext';

function useLocalizeTo() {
  const { localize } = useLanguage();
  return useCallback(
    (to: To): To => {
      if (typeof to === 'string') return to.startsWith('/') ? localize(to) : to;
      if (to && typeof to === 'object' && typeof to.pathname === 'string' && to.pathname.startsWith('/')) {
        return { ...to, pathname: localize(to.pathname) };
      }
      return to;
    },
    [localize],
  );
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LocaleLink({ to, ...rest }, ref) {
  const localizeTo = useLocalizeTo();
  return <RouterLink ref={ref} to={localizeTo(to)} {...rest} />;
});

export function useNavigate() {
  const navigate = useRouterNavigate();
  const localizeTo = useLocalizeTo();
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') return navigate(to);
      return navigate(localizeTo(to), options);
    },
    [navigate, localizeTo],
  );
}
